import React, { useEffect, useState, forwardRef } from 'react';
import Snackbar, { SnackbarProps } from '@material-ui/core/Snackbar';
import type { Color } from '@material-ui/lab/Alert';
import Slide, { SlideProps } from '@material-ui/core/Slide';

import Notification from 'components/Notification';
import { NotificationSeverity } from 'state/notifications/notificationsSlice';
import type { Notification as NotificationType } from 'state/notifications/notificationsSlice';

type NotificationsProps = SnackbarProps & {
  notifications: NotificationType[];
  removeNotification: (id: string) => void;
};

const SlideFromRight = forwardRef<typeof Slide, SlideProps>((props, ref) => (
  /* eslint-disable react/jsx-props-no-spreading */
  <Slide {...props} ref={ref} direction="left" />
));

const Notifications: React.FC<NotificationsProps> = ({
  notifications,
  removeNotification,
}) => {
  const [open, setOpen] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<
    NotificationType | undefined
  >(undefined);

  const handleTransitionEnd = (notificationId: string) => () => {
    removeNotification(notificationId);
    setCurrentNotification(undefined);
  };

  const closeSnackbar = () => setOpen(false);

  const handleClose: SnackbarProps['onClose'] = (_event, reason) =>
    reason === 'clickaway' && !currentNotification?.isClosableByUser
      ? null
      : closeSnackbar();

  useEffect(() => {
    const latestNotification = notifications[notifications.length - 1];
    if (latestNotification && !currentNotification) {
      // no notification currently displayed, display latest notification
      setCurrentNotification(latestNotification);
      setOpen(true);
    } else if (
      latestNotification &&
      currentNotification !== latestNotification &&
      open
    ) {
      // already a notification displayed, hide it (then triggers handleTransitionEnd
      // to remove currently displayed notification from the store)
      setOpen(false);
    }
  }, [notifications, notifications.length, currentNotification, open]);

  return (
    <>
      {currentNotification && (
        <div>
          <Snackbar
            open={open}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            onClose={handleClose}
            key={currentNotification.id}
            autoHideDuration={5000}
            TransitionComponent={SlideFromRight}
            TransitionProps={{
              onExited: handleTransitionEnd(currentNotification.id),
            }}
          >
            <Notification
              message={currentNotification.message}
              severity={
                NotificationSeverity[
                  currentNotification.notificationSeverity
                ] as Color
              }
              onClose={closeSnackbar}
            />
          </Snackbar>
        </div>
      )}
    </>
  );
};

export default Notifications;
